<template>
  <div v-if="hasMessages">
    <template v-if="isBanner">
      <transition-group appear v-for="(messageObj, type) in fullMessages" :key="type.toString()">
        <div :class="classes(type)" class="alert alert-dismissible" role="alert" :key="type.toString()">
          <row>
            <column>
              <slot>
                <ul>
<!--                  Use v-html to support links in flash messages -->
                  <li v-for="m in messageObj.messages" v-html="m"></li>
                </ul>
              </slot>
            </column>
            <suffix>
              <button v-if="messageObj.dismissible" @click="close(type)" type="button" class="btn-icon grey-to-red close">
                <icon class="times"></icon>
                <span class="sr-only">Dismiss Alert Button</span>
              </button>
            </suffix>
          </row>
        </div>
      </transition-group>
    </template>
    <modal v-else
      :state="modalStateFor(flashModalId)"
      @toggle-modal="hideModal(flashModalId)"
      :class="flashDetails.modalMaskClass">
      <h3 v-if="flashDetails.title" class="modal-title">{{flashDetails.title}}</h3>
      <h4 v-if="flashDetails.subtitle">{{flashDetails.subtitle}}</h4>
      <p v-if="flashDetails.description">{{flashDetails.description}}</p>
      <scroll-area class="lg">
        <template v-for="(messageObj, index) in fullMessages">
          <component :is="messageTag"
                     v-for="message in messageObj.messages"
                     :key="guidForMessageKey"
                     :class="flashDetails.class"
                     v-html="message" />
        </template>
      </scroll-area>
      <div class="cta">
        <button
          v-for="button in flashDetails.buttons"
          type="button"
          @click="boundAction(button)"
          :class="button.class">{{button.text}}</button>
      </div>
    </modal>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import {Hash} from '~/interfaces/hashInterfaces'
  import {FlashMode, FlashStyle} from "~/helpers/flashHelper";
  import Guid from "~/helpers/guid";
  import {UrlHelper} from "~/helpers/urlHelper";
  import {BooleanHelper} from "~/helpers/booleanHelper"
  import {ArrayHelper} from "~/helpers/arrayHelper"

  interface IFlashKeySummary {
    messages: string[]
    dismissible: boolean
  }

  export default Vue.extend({
    props: {
      fixed: {
        type: Boolean,
        default: false,
      },
      flashStoreName: {
        type: String,
        default: 'flashStore'
      },
    },
    data: function () {
      return {
        flash: {},
        flashModalId: "FLASHMODAL"
      };
    },
    mounted() {
      this.flash = this.flashStore.flash
      this.getFlash();
      this.deleteFlashQuery();
    },
    computed: {
      flashStore() {
        return this.$store.state[this.flashStoreName]
      },
      flashDetails(): {buttons: Array<{}>} {
        return this.flashStore.flashDetails
      },
      isBanner(): boolean {
        return this.mode === FlashMode.Banner
      },
      isModal(): boolean {
        return this.mode === FlashMode.Modal
      },
      mode(): FlashMode {
        return this.flashStore.mode
      },
      fullMessages(): { [key: string]: IFlashKeySummary } {
        let flashes = {};
        for (let key in this.flash) {
          flashes[key] = ArrayHelper.wrap(this.flash[key])
          flashes[key] = flashes[key].reduce((agg, flash) => {
            if (flash instanceof Object) {
              agg.messages.push(flash.message)
              if (agg.dismissible && BooleanHelper.isStrictBoolean(flash.dismissible)) {
                agg.dismissible = flash.dismissable
              }
            } else {
              agg.messages.push(flash)
            }
            return agg
          }, { messages: [], dismissible: true })
          if (!flashes[key].messages.length) delete flashes[key]
        }
        return flashes;
      },
      hasMessages(): boolean {
        return Object.values<IFlashKeySummary>(this.fullMessages).some(obj => obj.messages.length > 0)
      },
      messageTag(): string {
        switch(this.flashDetails.styleMode) {
          case FlashStyle.Code:
            return 'pre'
          default:
            return 'p'
        }
      },
      guidForMessageKey(): string {
        return Guid.create();
      },
    },
    methods: {
      boundAction(button) {
        button.action.bind(this)()
      },
      close(type: string): void {
        Vue.delete(this.flash, type);
      },
      getFlash(): void {
        this.flash = Object.assign({}, this.flashStore.flash);
        if (this.isModal) this.showModal(this.flashModalId)
        this.$store.commit(`${this.flashStoreName}/reset`);
      },
      classes(type: string): Hash<boolean> {
        return {
          'sticky': this.fixed,
          'danger': type === 'alert',
          'info': type === 'notice',
          'edit': type === 'edit',
          'warning': type === 'warning',
        }
      },
      // prevents the flash from showing again on page refresh/reload in the form submission pack
      deleteFlashQuery(): void {
        let path = window.location.href.split('?')[0];
        const query = UrlHelper.parseQuery(window.location.href);
        if ('flash' in query) { delete query['flash'] }
        let url = path;
        if (Object.keys(query).length > 0) {
          // '%' needs to be decoded before using history.replaceState...also, '&' and '+' are not always decoded, so must be handled special
          Object.keys(query).forEach(q => {
            query[q] = decodeURIComponent(query[q])
            .replace(/%26/g, '&')
            .replace(/\+/g, ' ')
          })
          url = `${path}?${$.param(query)}`
        }
        history.replaceState({}, document.title, `${url}`);
      }
    },
    watch: {
      'flashStore.trigger': function (newVal, oldVal) {
        if (newVal) {
          this.getFlash();
        }
      },
    }
  });
</script>
